/**
 * KPIs de la Actividad de usuarios (presentacional).
 *
 * Réplica de la fila de KPIs del SSR (`user_activity.html`): total de
 * interacciones, usuarios activos, módulo más usado y usuario más activo. Sin
 * lógica de datos: recibe el resumen ya cargado y deriva los KPIs con los helpers
 * de `format.ts`.
 */
import { Card, CardContent } from "@/components/ui";

import { moduleLabel, topModule, topUser } from "../format";
import type { ActivitySummary } from "../types";

interface KpiProps {
  label: string;
  value: string | number;
  // `exactOptionalPropertyTypes`: una prop opcional pasada como `undefined`
  // explícito (KPIs sin submensaje) requiere incluir `undefined` en el tipo.
  sub?: string | undefined;
}

function Kpi({ label, value, sub }: KpiProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </span>
        <span className="text-2xl font-bold text-slate-900">{value}</span>
        {sub ? <span className="text-xs text-slate-500">{sub}</span> : null}
      </CardContent>
    </Card>
  );
}

interface ActivityKpisProps {
  summary: ActivitySummary;
}

export function ActivityKpis({ summary }: ActivityKpisProps) {
  const mod = topModule(summary);
  const user = topUser(summary);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Kpi label="Total interacciones" value={summary.total} sub="en el período" />
      <Kpi label="Usuarios activos" value={summary.users.length} sub="distintos" />
      <Kpi
        label="Módulo más usado"
        value={mod ? moduleLabel(mod.key) : "—"}
        sub={mod ? `${mod.count} veces` : undefined}
      />
      <Kpi
        label="Más activo"
        value={user ? user.name : "—"}
        sub={user ? `${user.total} interacciones` : undefined}
      />
    </div>
  );
}
